import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  category, 
  categories, 
  onConfirm 
}) => {
  const [reassignCategoryId, setReassignCategoryId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    
    try {
      await onConfirm(category.id, reassignCategoryId);
      onClose();
      setReassignCategoryId('');
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setReassignCategoryId('');
    onClose();
  };

  const hasProducts = category?.productCount > 0;
  const hasSubcategories = category?.children && category.children.length > 0;

  const reassignOptions = categories
    .filter(cat => cat.id !== category?.id)
    .map(cat => ({
      value: cat.id,
      label: cat.name,
      description: `${cat.productCount} products`
    }));

  if (!isOpen || !category) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-elevation-3 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-md bg-error/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Delete Category</h2>
              <p className="text-sm text-muted-foreground">This action cannot be undone</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isProcessing}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 rounded-md bg-surface flex items-center justify-center">
                <Icon name={category.icon || 'Folder'} size={16} className="text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">{category.name}</h3>
                <p className="text-sm text-muted-foreground">{category.description}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Products:</span>
                <span className="ml-2 font-medium text-foreground">{category.productCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Subcategories:</span>
                <span className="ml-2 font-medium text-foreground">{category.children?.length || 0}</span>
              </div>
            </div>
          </div>

          {(hasProducts || hasSubcategories) && (
            <div className="space-y-4">
              <div className="bg-warning/10 border border-warning/20 rounded-md p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-warning mb-1">Category Contains Data</h4>
                    <p className="text-sm text-warning/80">
                      This category contains {category.productCount} products
                      {hasSubcategories && ` and ${category.children.length} subcategories`}.
                      You need to reassign them before deletion.
                    </p>
                  </div>
                </div>
              </div>

              <Select
                label="Reassign to Category"
                options={[
                  { value: '', label: 'Move to Root Level' },
                  ...reassignOptions
                ]}
                value={reassignCategoryId}
                onChange={setReassignCategoryId}
                placeholder="Select target category"
                disabled={isProcessing}
                description="Products and subcategories will be moved to this category"
              />
            </div>
          )}

          {!hasProducts && !hasSubcategories && (
            <div className="bg-error/10 border border-error/20 rounded-md p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Trash2" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-error mb-1">Confirm Deletion</h4>
                  <p className="text-sm text-error/80">
                    Are you sure you want to delete "{category.name}"? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isProcessing || ((hasProducts || hasSubcategories) && !reassignCategoryId)}
            loading={isProcessing}
            iconName="Trash2"
            iconPosition="left"
          >
            Delete Category
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;